import { RefObject, useCallback, useEffect, useState } from 'react';

interface FileDragging {
    isDragging: boolean;
    itemCount: number;
}

export const useDropTarget = (
    onFilesDropped: (files?: FileList) => void,
    [dropTarget, ...dependencies]: [RefObject<HTMLElement>, ...any],
): FileDragging => {
    const [itemCount, setItemCount] = useState(0);
    const isDragging = itemCount !== 0;
    useEffect(() => setItemCount(0), [setItemCount, dropTarget]);

    const onDragEnter = useCallback(
        (e: DragEvent) => {
            if (e.target === dropTarget.current) {
                setItemCount(e.dataTransfer?.items.length ?? 0);
                e.stopPropagation();
                e.preventDefault();
            }
        },
        [setItemCount, dropTarget],
    );
    const onDragOver = useCallback(
        (e: DragEvent) => {
            if (isDragging) {
                e.preventDefault();
                e.stopPropagation();
            }
        },
        [isDragging],
    );
    const onDragLeave = useCallback(
        (e: DragEvent) => {
            if (e.target === dropTarget.current) {
                setItemCount(0);
                e.stopPropagation();
                e.preventDefault();
            }
        },
        [setItemCount, dropTarget],
    );
    const onDrop = useCallback(
        (e: DragEvent) => {
            if (isDragging) {
                onFilesDropped(e.dataTransfer?.files);
                setItemCount(0);
                e.preventDefault();
            }
        },
        // dependencies are the deps of onFileDropped as in useCallback
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setItemCount, isDragging, dependencies],
    );

    useEffect(() => {
        window.addEventListener('dragleave', onDragLeave);
        window.addEventListener('dragenter', onDragEnter);
        window.addEventListener('dragover', onDragOver);
        window.addEventListener('drop', onDrop);
        return () => {
            window.removeEventListener('dragleave', onDragLeave);
            window.removeEventListener('dragenter', onDragEnter);
            window.removeEventListener('dragover', onDragOver);
            window.removeEventListener('drop', onDrop);
        };
    }, [onDragEnter, onDragLeave, onDragOver, onDrop]);

    return {
        isDragging,
        itemCount,
    };
};
